import { Alert, Button, Empty, Spin, Table, message } from 'antd'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { SPARTACUS_PAGE_SIZE_OPTIONS } from 'src/constants/spartacus'
import { fetchSpartacusVenueUsers } from 'src/utils/spartacusApi'
import { copyVenueUserEmails } from 'src/utils/spartacusClipboard'
import { getSpartacusLoadingPresentation } from 'src/utils/spartacusLoading'
import {
  formatSpartacusCustomerNotesButtonLabel,
  mapSpartacusVenueUsersResponse,
  mapTableSorterToSpartacusSort,
} from 'src/utils/spartacusMappers'
import { buildSpartacusTableColumns } from 'src/utils/spartacusTableColumns'
import { openVenueDashboard } from 'src/utils/spartacusVenueActions'
import styled from 'styled-components/macro'
import SpartacusVenueNotesModal from './SpartacusVenueNotesModal'
import SpartacusVenueUsersModal from './SpartacusVenueUsersModal'

function SpartacusVenueTable({
  venues,
  pagination,
  sortBy,
  sortDir,
  loading,
  error,
  onPaginationChange,
  onSortChange,
  onAddToCatchBoard,
}) {
  const [usersByVenue, setUsersByVenue] = useState({})
  const usersByVenueRef = useRef({})
  const [usersLoadingByVenue, setUsersLoadingByVenue] = useState({})
  const [usersErrorByVenue, setUsersErrorByVenue] = useState({})
  const [copyingVenueId, setCopyingVenueId] = useState(null)
  const [activeUsersVenue, setActiveUsersVenue] = useState(null)
  const [activeNotesVenue, setActiveNotesVenue] = useState(null)
  const [notesMetaByVenue, setNotesMetaByVenue] = useState({})

  const loadVenueUsers = useCallback(async venueId => {
    if (!venueId) {
      return null
    }

    if (usersByVenueRef.current[venueId]) {
      return usersByVenueRef.current[venueId]
    }

    setUsersLoadingByVenue(prev => ({ ...prev, [venueId]: true }))
    setUsersErrorByVenue(prev => ({ ...prev, [venueId]: null }))

    try {
      const response = await fetchSpartacusVenueUsers(venueId)
      const mapped = mapSpartacusVenueUsersResponse(response)
      usersByVenueRef.current[venueId] = mapped
      setUsersByVenue(prev => ({ ...prev, [venueId]: mapped }))
      return mapped
    } catch (err) {
      const errorMessage = err.message || 'Failed to load venue users'
      setUsersErrorByVenue(prev => ({ ...prev, [venueId]: errorMessage }))
      return null
    } finally {
      setUsersLoadingByVenue(prev => ({ ...prev, [venueId]: false }))
    }
  }, [])

  const handleViewUsers = useCallback(
    record => {
      setActiveUsersVenue({
        venueId: record.venueId,
        venueName: record.name,
        venuePhone: record.phone,
      })
      loadVenueUsers(record.venueId)
    },
    [loadVenueUsers],
  )

  const handleCloseUsersModal = useCallback(() => {
    setActiveUsersVenue(null)
  }, [])

  const handleOpenNotes = useCallback(record => {
    setActiveNotesVenue({
      venueId: record.venueId,
      venueName: record.name,
    })
  }, [])

  const handleCloseNotesModal = useCallback(() => {
    setActiveNotesVenue(null)
  }, [])

  const handleNotesChanged = useCallback(
    (venueId, change = {}) => {
      setNotesMetaByVenue(prev => {
        const venue = venues.find(item => item.venueId === venueId)
        const current = prev[venueId] || {
          notesCount: Number(venue?.notesCount) || 0,
          hasRecentNote: Boolean(venue?.hasRecentNote),
          latestNoteAt: venue?.latestNoteAt || null,
        }
        const didCreate = change.type === 'created'

        return {
          ...prev,
          [venueId]: {
            notesCount: didCreate
              ? (Number(current.notesCount) || 0) + 1
              : Number(current.notesCount) || 0,
            hasRecentNote: didCreate ? true : Boolean(current.hasRecentNote),
            latestNoteAt: didCreate
              ? new Date().toISOString()
              : current.latestNoteAt || null,
          },
        }
      })
    },
    [venues],
  )

  const getNotesMeta = useCallback(
    record => {
      const override = notesMetaByVenue[record.venueId]
      if (override) {
        return override
      }

      return {
        notesCount: Number(record.notesCount) || 0,
        hasRecentNote: Boolean(record.hasRecentNote),
        latestNoteAt: record.latestNoteAt || null,
      }
    },
    [notesMetaByVenue],
  )

  const handleCopyEmails = useCallback(
    async venueId => {
      setCopyingVenueId(venueId)

      try {
        const usersData =
          usersByVenue[venueId] || (await loadVenueUsers(venueId))

        if (!usersData?.users?.length) {
          message.warning('No user emails found for this venue')
          return
        }

        const emails = await copyVenueUserEmails(usersData.users)
        message.success(
          `Copied ${emails.length} email${
            emails.length === 1 ? '' : 's'
          } to clipboard`,
        )
      } catch (err) {
        message.error(err.message || 'Could not copy emails')
      } finally {
        setCopyingVenueId(null)
      }
    },
    [loadVenueUsers, usersByVenue],
  )

  const handleAddToCatchBoard = useCallback(
    record => {
      if (!onAddToCatchBoard) {
        return
      }
      onAddToCatchBoard(record)
    },
    [onAddToCatchBoard],
  )

  const handleOpenVenue = useCallback(venueId => {
    try {
      openVenueDashboard(venueId)
    } catch (err) {
      message.error(err.message || 'Could not open venue')
    }
  }, [])

  const columns = useMemo(() => {
    const baseColumns = buildSpartacusTableColumns({ sortBy, sortDir })

    return [
      ...baseColumns,
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 200,
        render: (_value, record) => {
          const notesMeta = getNotesMeta(record)

          return (
            <ActionButtons>
              {onAddToCatchBoard ? (
                <Button
                  size='small'
                  type='primary'
                  onClick={() => handleAddToCatchBoard(record)}
                  data-testid={`add-to-catch-board-${record.venueId}`}
                >
                  Add to Catch Board
                </Button>
              ) : null}
              <Button size='small' onClick={() => handleViewUsers(record)}>
                View users
              </Button>
              <Button
                size='small'
                loading={copyingVenueId === record.venueId}
                onClick={() => handleCopyEmails(record.venueId)}
              >
                Copy emails
              </Button>
              <Button
                size='small'
                onClick={() => handleOpenVenue(record.venueId)}
              >
                Open venue
              </Button>
              <NotesButton
                size='small'
                $recent={notesMeta.hasRecentNote}
                onClick={() => handleOpenNotes(record)}
                data-testid={`customer-notes-${record.venueId}`}
                data-recent={notesMeta.hasRecentNote ? 'true' : 'false'}
              >
                {formatSpartacusCustomerNotesButtonLabel(notesMeta.notesCount)}
              </NotesButton>
            </ActionButtons>
          )
        },
      },
    ]
  }, [
    sortBy,
    sortDir,
    copyingVenueId,
    getNotesMeta,
    handleAddToCatchBoard,
    handleCopyEmails,
    handleOpenNotes,
    handleOpenVenue,
    handleViewUsers,
    onAddToCatchBoard,
  ])

  const handleTableChange = (nextPagination, _filters, sorter) => {
    const nextSort = mapTableSorterToSpartacusSort(sorter)
    const sortChanged =
      nextSort && (nextSort.sortBy !== sortBy || nextSort.sortDir !== sortDir)

    if (sortChanged) {
      onSortChange(nextSort)
      return
    }

    const pageChanged =
      nextPagination.current !== pagination.page ||
      nextPagination.pageSize !== pagination.pageSize

    if (pageChanged) {
      onPaginationChange({
        page: nextPagination.current,
        pageSize: nextPagination.pageSize,
      })
    }
  }

  const emptyText =
    error && !venues.length ? (
      <Empty description='Unable to load venues' />
    ) : (
      <Empty description='No venues match the current filters' />
    )

  const { showInitialLoader, showRefreshOverlay } =
    getSpartacusLoadingPresentation({
      loading,
      hasData: venues.length > 0,
    })

  const activeVenueId = activeUsersVenue?.venueId

  return (
    <TableWrap>
      {error ? <StyledAlert type='error' showIcon message={error} /> : null}
      <TableContainer>
        <Table
          rowKey='key'
          columns={columns}
          dataSource={venues}
          loading={showInitialLoader}
          scroll={{ x: 2480 }}
          locale={{ emptyText }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: SPARTACUS_PAGE_SIZE_OPTIONS,
            position: 'bottom',
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} venues`,
          }}
        />
        {showRefreshOverlay ? (
          <TableRefreshOverlay aria-hidden='true'>
            <Spin />
          </TableRefreshOverlay>
        ) : null}
      </TableContainer>

      <SpartacusVenueUsersModal
        visible={!!activeUsersVenue}
        venueName={activeUsersVenue?.venueName}
        venuePhone={activeUsersVenue?.venuePhone}
        usersData={activeVenueId ? usersByVenue[activeVenueId] : null}
        loading={activeVenueId ? usersLoadingByVenue[activeVenueId] : false}
        error={activeVenueId ? usersErrorByVenue[activeVenueId] : null}
        copying={activeVenueId ? copyingVenueId === activeVenueId : false}
        onClose={handleCloseUsersModal}
        onCopyEmails={() => handleCopyEmails(activeVenueId)}
      />

      <SpartacusVenueNotesModal
        visible={!!activeNotesVenue}
        venueId={activeNotesVenue?.venueId}
        venueName={activeNotesVenue?.venueName}
        onClose={handleCloseNotesModal}
        onNotesChanged={handleNotesChanged}
      />
    </TableWrap>
  )
}

const TableWrap = styled.div`
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;

  .ant-table {
    font-size: 13px;
  }

  .ant-table-thead > tr > th {
    overflow-wrap: normal;
    word-break: keep-all;
  }

  .ant-table-fixed-left .ant-table-tbody > tr > td:last-child,
  .ant-table-fixed-left .ant-table-thead > tr > th:last-child {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 767px) {
    .ant-table {
      font-size: 14px;
    }

    .ant-pagination {
      margin: 12px 0 0;
    }
  }
`

const TableContainer = styled.div`
  position: relative;
`

const TableRefreshOverlay = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  z-index: 2;
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 767px) {
    gap: 6px;
    min-width: 148px;

    .ant-btn {
      font-size: 14px;
      min-height: 40px;
      padding: 0 10px;
      white-space: normal;
    }
  }
`

const NotesButton = styled(Button).withConfig({
  shouldForwardProp: prop => prop !== '$recent',
})`
  ${({ $recent, theme }) =>
    $recent
      ? `
    background: ${theme.colours.lettuceGreen || '#b5d922'};
    border-color: ${theme.colours.lettuceGreen || '#b5d922'};
    color: ${theme.colours.seaweedGreen || '#1f2a1a'};
    font-weight: 600;

    &:hover,
    &:focus {
      background: ${theme.colours.lettuceGreen || '#b5d922'};
      border-color: ${theme.colours.lettuceGreen || '#b5d922'};
      color: ${theme.colours.seaweedGreen || '#1f2a1a'};
    }
  `
      : ''}
`

export default SpartacusVenueTable
